'use client';

import React, { useState } from 'react';
import { EditMetricItem } from '../EditMetricItem';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { Dialog, Button } from 'src/components';
import { useDeleteStrategyMetricMutation } from 'src/api/redux/strategy';
import { formatExecutionParams } from './formatExecutionParams';

type ColumnItem = {
  id?: string | number;
  label: string;
  description: string;
  disabled?: boolean;
  execution_params?: Record<string, any>;
  summary?: string;
};

interface MetricItemProps {
  item: ColumnItem;
  strategyId: string;
  statusLabelMap?: Map<string, string>;
}

const MetricItem: React.FC<MetricItemProps> = ({ item, strategyId, statusLabelMap }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteStrategyMetric, { isLoading: isDeleting }] =
    useDeleteStrategyMetricMutation();

  const handleDelete = async () => {
    if (!item.id) return;

    try {
      await deleteStrategyMetric({
        metricId: item.id.toString(),
        strategyId: strategyId,
      }).unwrap();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Failed to delete metric:', error);
    }
  };

  const displayDescription =
    formatExecutionParams(item.execution_params, statusLabelMap) || item.description;

  const testId = `metric-item-${
    item.id || item.label.toLowerCase().replace(/\s+/g, '-')
  }`;

  if (isEditing) {
    return (
      <EditMetricItem
        item={item}
        strategyId={strategyId}
        statusLabelMap={statusLabelMap}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <>
      <li
        className="group grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4 w-full"
        data-testid={testId}
        aria-label={`Metric column: ${item.label}`}
      >
        <div
          className={`w-full h-9 px-3 py-2 rounded-full border border-neutral-200 inline-flex justify-between items-center gap-2.5 ${
            item.disabled ? 'bg-level-2' : 'bg-common-White'
          }`}
        >
          <div className="flex-1 min-w-0 pr-2 flex justify-start items-center gap-1">
            <span
              className={`text-sm font-normal font-['Inter'] leading-tight truncate ${
                item.disabled ? 'text-neutral-400' : 'text-primary'
              }`}
              data-testid={`${testId}-label`}
            >
              {item.label}
            </span>
          </div>
          {!item.disabled && (
            <div
              className="flex items-center gap-1"
              role="group"
              aria-label="Metric actions"
            >
              <button
                onClick={() => setIsEditing(true)}
                className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200"
                aria-label={`Edit ${item.label}`}
                data-testid={`${testId}-edit-button`}
              >
                <Icon icon={faPencil} className="w-3 h-3" />
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 transition-all duration-200"
                aria-label={`Delete ${item.label}`}
                data-testid={`${testId}-delete-button`}
              >
                <Icon icon={faTrash} className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        <p
          className="text-secondary text-xs font-normal font-['Inter'] leading-none flex items-center"
          data-testid={`${testId}-description`}
        >
          {displayDescription}
        </p>
      </li>

      <Dialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        showCloseButton
        aria-labelledby={`delete-dialog-title-${item.id}`}
        aria-describedby={`delete-dialog-description-${item.id}`}
      >
        <div className="px-6 py-4" data-testid={`${testId}-delete-dialog`}>
          <div className="flex flex-col items-center text-center space-y-4">
            <div
              className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center ring-8 ring-red-50/30"
              aria-hidden="true"
            >
              <Icon
                icon={faExclamationTriangle}
                className="text-red-600 text-2xl"
              />
            </div>

            <div className="space-y-2">
              <h3
                id={`delete-dialog-title-${item.id}`}
                className="text-lg font-semibold text-gray-900"
              >
                Delete column?
              </h3>
              <p
                id={`delete-dialog-description-${item.id}`}
                className="text-sm text-gray-600 max-w-sm"
              >
                You&apos;re about to permanently delete{' '}
                <span className="font-medium text-gray-900">
                  &ldquo;{item.label}&rdquo;
                </span>
                . This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 pt-2 w-full">
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 hover:bg-gray-50 transition-colors"
                disabled={isDeleting}
                data-testid={`${testId}-delete-cancel-button`}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={handleDelete}
                className="flex-1 hover:bg-red-700 transition-all hover:shadow-lg"
                disabled={isDeleting}
                data-testid={`${testId}-delete-confirm-button`}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default MetricItem;
